import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEAD_RATING_DEFS,
  LEAD_RATING_MAP,
  LEAD_STATUS_DEFS,
  LEAD_BUCKET_DEFS,
  LEAD_STATUS_MAP,
  type LeadBucket,
  type LeadStatus,
  type BulkResult,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { LeadEditor } from './LeadEditor';
import { InsightCards, type InsightItem } from '../../components/Insights';
import {
  CardPickerButton,
  applyChoice,
  useCardChoice,
  type CardChoice,
} from '../../components/CardPicker';
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from '../../components/ColumnPicker';
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import { LeadBulkBar, LeadBulkResult } from './LeadBulkBar';
import { activeFields, useCustomFields } from '../../components/CustomFields';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, dateTime, money, plural, relative, WORDS } from '../../lib/format';
import { Icon, type IconName } from '../../components/Icon';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from '../../components/ui';
import { Avatar, OwnerChip } from '../../components/People';
import { ReachIcons } from '../../components/Reach';
import { Briefing } from '../../components/Briefing';
import { LeadQuickActions } from './LeadQuickActions';

/** أيقونة كل دلو — تخصّ هذه الواجهة فلا موضع لها في الكتالوج المشترك. */
const BUCKET_ICONS: Record<LeadBucket, IconName> = {
  today: 'plus',
  hot: 'zap',
  followup: 'alert',
  converted: 'check',
  unassigned: 'user',
};

/** وأيقونة كل حالة. */
const STATUS_ICONS: Record<LeadStatus, IconName> = {
  NEW: 'inbox',
  CONTACTED: 'phone',
  WORKING: 'refresh',
  QUALIFIED: 'check',
  CONVERTED: 'zap',
  UNQUALIFIED: 'x',
  JUNK: 'trash',
};

/**
 * البطاقات المعروضة قبل أن يختار المستخدم.
 *
 * الدلاء وحدها: تجيب عن سؤال الصباح، والحالات تفصيلٌ يطلبه من يريده. وخمس
 * بطاقات تُقرأ بنظرة، واثنتا عشرة تُتخطّى بالعين كلها.
 */
const DEFAULT_CARDS = LEAD_BUCKET_DEFS.map((d) => d.key as string);

/** ما يُعرض في نافذة الاختيار. */
const cardChoices: CardChoice[] = [
  ...LEAD_BUCKET_DEFS.map((d) => ({ key: d.key, label: d.label, group: 'حسب المتابعة' })),
  ...LEAD_STATUS_DEFS.map((s) => ({ key: s.key, label: s.label, group: 'حسب الحالة' })),
];

interface LeadRow {
  id: string;
  name: string;
  company: string | null;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  altPhone: string | null;
  status: LeadStatus;
  rating: 'HOT' | 'WARM' | 'COLD';
  source: string | null;
  industry: string | null;
  city: string | null;
  address: string | null;
  notes: string | null;
  value: string | null;
  avatarUrl: string | null;
  ownerId: string | null;
  lastContactedAt: string | null;
  nextActionAt: string | null;
  createdAt: string;
  customFields: Record<string, unknown> | null;
}

/**
 * الأعمدة الثابتة — كل ما في نموذج العميل يصلح عمودًا.
 *
 * والحقول المخصَّصة تُلحق بها في وقت التشغيل: هي بيانات العميل كغيرها،
 * وحصرُ الاختيار في المدمَج يجعل من أضاف حقلًا لا يجده حيث يُقرأ.
 *
 * `fixed` على الاسم وحده: هو ما يُعرف به الصفّ، وإخفاؤه يترك جدولًا من
 * أرقامٍ لا يُعرف لمن هي.
 */
const BASE_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'الاسم', fixed: true },
  { key: 'company', label: 'الشركة' },
  { key: 'jobTitle', label: 'المسمى الوظيفي' },
  { key: 'ownerId', label: 'المسؤول' },
  { key: 'reach', label: 'وسيلة التواصل' },
  { key: 'status', label: 'الحالة' },
  { key: 'rating', label: 'الأولوية' },
  { key: 'value', label: 'القيمة' },
  { key: 'lastContactedAt', label: 'آخر تواصل' },
  { key: 'source', label: 'المصدر' },
  { key: 'phone', label: 'الهاتف' },
  { key: 'altPhone', label: 'هاتف آخر' },
  { key: 'email', label: 'البريد' },
  { key: 'industry', label: 'النشاط' },
  { key: 'city', label: 'المدينة' },
  { key: 'address', label: 'العنوان' },
  { key: 'notes', label: 'ملاحظات' },
  { key: 'nextActionAt', label: 'الإجراء القادم' },
  { key: 'createdAt', label: 'أُضيف في' },
];

/**
 * ما يُعرض قبل أن يختار المستخدم.
 *
 * هي أعمدة الجدول كما كانت — فمن لم يفتح النافذة لا يرى شيئًا تغيّر.
 */
/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في leads.service.ts. */
const LEAD_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الاسم', kind: 'text', sortable: true },
  { key: 'company', label: 'الشركة', kind: 'text' },
  { key: 'jobTitle', label: 'المسمى الوظيفي', kind: 'text' },
  { key: 'ownerId', label: 'المسؤول', kind: 'user', allowNone: true },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    sortable: true,
    options: LEAD_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  {
    key: 'rating',
    label: 'الأولوية',
    kind: 'enum',
    options: LEAD_RATING_DEFS.map((r) => ({ value: r.key, label: r.label })),
  },
  { key: 'value', label: 'القيمة', kind: 'number', sortable: true },
  { key: 'lastContactedAt', label: 'آخر تواصل', kind: 'date' },
  { key: 'source', label: 'المصدر', kind: 'text' },
  { key: 'phone', label: 'الهاتف', kind: 'text' },
  { key: 'email', label: 'البريد', kind: 'text' },
  { key: 'industry', label: 'النشاط', kind: 'text' },
  { key: 'city', label: 'المدينة', kind: 'text' },
  { key: 'notes', label: 'ملاحظات', kind: 'text' },
  { key: 'nextActionAt', label: 'الإجراء القادم', kind: 'date', sortable: true },
  { key: 'createdAt', label: 'أُضيف في', kind: 'date', sortable: true },
];

const DEFAULT_HIDDEN = [
  'jobTitle',
  'phone',
  'altPhone',
  'email',
  'industry',
  'city',
  'address',
  'notes',
  'nextActionAt',
  'createdAt',
];

interface LeadPage {
  items: LeadRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export function LeadsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);

  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [creating, setCreating] = useState(false);
  /*
   * التحديد بالصفحة لا عبر الصفحات.
   *
   * حفظه عبر التصفّح يجعل المستخدم يضغط «حذف» وهو يرى عشرة بينما المحدَّد
   * سبعون من صفحات لم يعد يذكرها.
   */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);

  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['leads', query],
    queryFn: () => api<LeadPage>(`/crm/leads${qs(query)}`),
  });

  const stats = useQuery({
    queryKey: ['leads', 'stats'],
    queryFn: () => api<Record<string, number>>('/crm/leads/stats'),
  });

  /*
   * `bucket` و`page` و`status` مستبعدة من استعلام الأعداد.
   *
   * الأول لأن كل بطاقة تعدّ دلوها وحده، والثاني لأن الأعداد للمجموعة كلها،
   * والثالث لأن بعض الدلاء تحمل حالتها في تعريفها — «تحوّلوا» محوَّلٌ
   * بالضرورة — فتمرير حالة فوقها يُنتج تقاطعًا فارغًا.
   */
  const { bucket: _b, page: _p, status: _s, ...bucketQuery } = query as Record<string, unknown>;

  const buckets = useQuery({
    queryKey: ['leads', 'buckets', bucketQuery],
    queryFn: () => api<Record<string, number>>(`/crm/leads/buckets${qs(bucketQuery)}`),
  });

  const total = stats.data ? Object.values(stats.data).reduce((a, b) => a + b, 0) : 0;
  const pageIds = list.data?.items.map((l) => l.id) ?? [];

  /*
   * البطاقات المتاحة: الدلاء الخمسة ثم الحالات السبع.
   *
   * الحالات تُقرأ من `/crm/leads/stats` وهو عدٌّ بالحالة، والدلاء من
   * `/crm/leads/buckets`. مصدران لأن السؤالين مختلفان — «في أي مرحلة هو؟»
   * و«ماذا أفعل اليوم؟».
   */
  const allCards: InsightItem[] = [
    ...LEAD_BUCKET_DEFS.map((d) => ({
      key: d.key,
      label: d.label,
      count: buckets.data?.[d.key] ?? 0,
      icon: BUCKET_ICONS[d.key],
      color: d.color,
      hint: d.hint,
    })),
    ...LEAD_STATUS_DEFS.map((s) => ({
      key: s.key,
      label: s.label,
      count: stats.data?.[s.key] ?? 0,
      icon: STATUS_ICONS[s.key],
      color: s.color,
      hint: `العملاء في مرحلة «${s.label}».`,
    })),
  ];

  const { chosen, setChosen, reset: resetCards } = useCardChoice('goyone.cards.leads', DEFAULT_CARDS);

  /*
   * الحقول المخصَّصة أعمدةٌ كغيرها.
   *
   * وتُلحق بالكتالوج في وقت التشغيل لأنها تُعرَّف من الإعدادات: حصرُ
   * الاختيار في المدمَج يجعل من أضاف حقلًا لا يجده حيث يُقرأ.
   */
  const customFields = useCustomFields('LEAD');
  const columns = useMemo<ColumnDef[]>(
    () => [
      ...BASE_COLUMNS,
      ...activeFields(customFields.data).map((f) => ({
        key: `cf:${f.key}`,
        label: f.label,
        group: 'حقول مخصَّصة',
      })),
    ],
    [customFields.data],
  );

  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice('goyone.columns.leads', columns, DEFAULT_HIDDEN);
  const colDrag = useColumnDrag(columnState, setColumnState);

  return (
    <div className="page">
      <PageHead
        title="العملاء المحتملون"
        subtitle={
          list.data
            ? `${plural(list.data.total, WORDS.record)} مطابقة من إجمالي ${total}`
            : 'قائمة العملاء قبل التأهيل'
        }
        /*
          زرّ الإضافة انتقل إلى الشريط اللاصق أسفله.
          ها هنا كان يختفي مع أول تمرير، فمن أراد إضافة عميل وهو يقرأ
          الصفحة عاد إلى أعلاها ليجده.
        */
      />

      {/*
        الموجز قبل البطاقات.
        البطاقات تقول «كم»، والموجز يقول «ابدأ بماذا» — والثاني هو سؤال
        من يفتح الشاشة أول النهار.
      */}
      <Briefing />

      {/*
        الدلاء والحالات معًا، والاختيار للمستخدم.
        الدلاء تجيب عن سؤال الصباح — ما الجديد؟ وأيّها ساخن؟ — والحالات
        تصنيفٌ لمرحلة العميل. وعرضهما جميعًا اثنتا عشرة بطاقة تملأ الشاشة
        قبل أول صفّ، وحصرُها في أحدهما يفرض على من يتابع «المستبعدين»
        ترتيبًا لا يعنيه.
      */}
      <InsightCards
        items={applyChoice(allCards, chosen)}
        active={state.bucket ?? state.status}
        onPick={(next) => {
          /* الدلو والحالة مرشّحان مختلفان: اختيار أحدهما يمسح الآخر. */
          const isBucket = LEAD_BUCKET_DEFS.some((d) => d.key === next);
          patch(
            next === undefined
              ? { bucket: undefined, status: undefined }
              : isBucket
                ? { bucket: next, status: undefined }
                : { status: next, bucket: undefined },
          );
        }}
        pending={buckets.isPending || stats.isPending}
        /* البطاقات تتقاطع: الساخن قد يكون جديدًا اليوم وبلا مسؤول معًا. */
        showShare={false}
      />

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الشركة أو الهاتف"
        />

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined, bucket: undefined })}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          {LEAD_STATUS_DEFS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.rating ?? ''}
          onChange={(e) => patch({ rating: e.target.value || undefined })}
          aria-label="الأولوية"
        >
          <option value="">كل الأولويات</option>
          {LEAD_RATING_DEFS.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>

        {(state.status || state.rating || state.q || state.bucket || state.f) && (
          <button
            className="btn btn-sm"
            onClick={() =>
              patch({
                status: undefined,
                rating: undefined,
                q: undefined,
                bucket: undefined,
                f: undefined,
              })
            }
          >
            <Icon name="x" size={14} />
            إلغاء التصفية
          </button>
        )}

        {/*
          الإضافة والاختيارات في الشريط اللاصق.
          الشريط يبقى ظاهرًا مهما نزل المستخدم، فيصير «أضِف عميلًا» في
          متناوله وهو في آخر الصفحة كما هو في أولها.
        */}
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={LEAD_FILTERS} filters={filters} onChange={setFilters} />
          <ColumnPickerButton
            columns={columns}
            state={columnState}
            onChange={setColumnState}
            onReset={resetColumns}
            touched={columnsTouched}
          />
          <CardPickerButton
            choices={cardChoices}
            chosen={chosen}
            onChange={setChosen}
            onReset={resetCards}
          />
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            <Icon name="plus" size={14} />
            عميل جديد
          </button>
        </span>
      </div>

      <FilterChips defs={LEAD_FILTERS} filters={filters} onChange={setFilters} />

      {picked.size > 0 && (
        <LeadBulkBar
          ids={[...picked]}
          onClear={() => setPicked(new Set())}
          onDone={(result) => {
            setPicked(new Set());
            setBulkResult(result);
          }}
        />
      )}

      <FilterScope
        defs={LEAD_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/crm/leads/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            {/*
              الإجراءات أولًا والتحديد آخرًا.

              الصفحة من اليمين إلى اليسار، فأوّل عمود يُرسم في أقصى اليمين.
              والقائمة هناك تقع تحت الإبهام ومسار العين، والتحديد في أقصى
              الشمال حيث لا يُضغط سهوًا وهو ماضٍ إلى «افتح».
            */}
            <thead>
              <tr>
                <th className="col-act" />
                {shownColumns.map((c) => (
                  <th key={c.key} {...colDrag.thProps(c)}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                <th className="col-pick">
                  {/*
                    تحديد الصفحة كلها.
                    الحالة الوسطى تُميّز «بعضها» عن «لا شيء» — بلا ذلك يبدو
                    المربّع فارغًا وقد حُدِّد نصف الصفحة.
                  */}
                  <input
                    type="checkbox"
                    aria-label="تحديد كل الصفحة"
                    checked={pageIds.length > 0 && pageIds.every((id) => picked.has(id))}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          pageIds.some((id) => picked.has(id)) &&
                          !pageIds.every((id) => picked.has(id));
                      }
                    }}
                    onChange={(e) => {
                      const next = new Set(picked);
                      if (e.target.checked) pageIds.forEach((id) => next.add(id));
                      else pageIds.forEach((id) => next.delete(id));
                      setPicked(next);
                    }}
                  />
                </th>
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + 2} />}

            {list.data && (
              <tbody>
                {list.data.items.map((lead) => (
                  <tr
                    key={lead.id}
                    className="clickable"
                    onClick={() => navigate(`/crm/leads/${lead.id}`)}
                  >
                    {/* الإجراءات لا تفتح الصفّ: القائمة توقف انتشار النقرة بنفسها. */}
                    <td className="col-act" onClick={(e) => e.stopPropagation()}>
                      <LeadQuickActions
                        lead={lead}
                        onChanged={() => void list.refetch()}
                      />
                    </td>

                    {shownColumns.map((c) => (
                      <Cell key={c.key} column={c} lead={lead} />
                    ))}

                    {/* النقر على المربّع لا يفتح الصف. */}
                    <td className="col-pick" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`تحديد ${lead.name}`}
                        checked={picked.has(lead.id)}
                        onChange={(e) => {
                          const next = new Set(picked);
                          if (e.target.checked) next.add(lead.id);
                          else next.delete(lead.id);
                          setPicked(next);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد سجلات مطابقة"
            text={
              state.q || state.status || state.rating || state.f
                ? 'جرّب توسيع نطاق البحث أو إلغاء التصفية.'
                : 'ابدأ بإضافة أول عميل محتمل.'
            }
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                عميل جديد
              </button>
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {creating && <LeadEditor onClose={() => setCreating(false)} />}

      {bulkResult && (
        <LeadBulkResult result={bulkResult} onClose={() => setBulkResult(null)} />
      )}
    </div>
  );
}


/* ==========================================================================
   الخلية
   ========================================================================== */

/**
 * خليّةٌ واحدة، يُختار شكلها بمفتاح العمود.
 *
 * الرسم في مكان واحد لا في `switch` داخل الجدول: الأعمدة صارت تُرتَّب
 * وتُخفى، فالخلية يجب أن تعرف نفسها بمفتاحها لا بموضعها.
 */
function Cell({ column, lead }: { column: ColumnDef; lead: LeadRow }) {
  /* الحقول المخصَّصة تُميَّز ببادئة: مفاتيحها يضبطها المستخدم وقد تصادف مدمَجًا. */
  if (column.key.startsWith('cf:')) {
    const raw = (lead.customFields ?? {})[column.key.slice(3)];
    const text =
      raw === null || raw === undefined || raw === ''
        ? '—'
        : raw === true
          ? 'نعم'
          : raw === false
            ? 'لا'
            : String(raw);
    return <td className="cell-mute">{text}</td>;
  }

  switch (column.key) {
    case 'name':
      /*
       * الصورة إن وُجدت، وإلا حروف الاسم بلون ثابت.
       *
       * والحروف ليست نقصًا يُسدّ: أكثر السجلات بلا صورة، واللون المشتقّ من
       * الاسم يجعل الصفّ يُميَّز قبل قراءته.
       */
      return (
        <td>
          <div className="row" style={{ gap: 8 }}>
            <Avatar name={lead.name} src={lead.avatarUrl} size={28} />
            <span className="cell-strong">{lead.name}</span>
          </div>
        </td>
      );

    case 'company':
      return <td className="cell-mute">{lead.company ?? '—'}</td>;

    case 'jobTitle':
      return <td className="cell-mute">{lead.jobTitle ?? '—'}</td>;

    case 'ownerId':
      return (
        <td>
          <OwnerChip ownerId={lead.ownerId} size={22} />
        </td>
      );

    case 'reach':
      return (
        <td onClick={(e) => e.stopPropagation()}>
          <ReachIcons
            name={lead.name}
            phone={lead.phone}
            email={lead.email}
            link={{ leadId: lead.id }}
          />
        </td>
      );

    case 'status':
      return (
        <td>
          <Badge kind={LEAD_STATUS_MAP[lead.status]?.kind}>
            {LEAD_STATUS_MAP[lead.status]?.label ?? lead.status}
          </Badge>
        </td>
      );

    case 'rating':
      return (
        <td>
          <Badge kind={LEAD_RATING_MAP[lead.rating]?.kind}>
            {LEAD_RATING_MAP[lead.rating]?.label ?? lead.rating}
          </Badge>
        </td>
      );

    case 'value':
      return <td className="num">{lead.value ? money(lead.value) : '—'}</td>;

    case 'lastContactedAt':
      return (
        <td className="cell-mute">
          {lead.lastContactedAt ? relative(lead.lastContactedAt) : 'لم يتم'}
        </td>
      );

    case 'source':
      return <td className="cell-mute">{lead.source ?? '—'}</td>;

    case 'phone':
      return <td className="cell-mute num">{lead.phone ?? '—'}</td>;

    case 'altPhone':
      return <td className="cell-mute num">{lead.altPhone ?? '—'}</td>;

    case 'email':
      return (
        <td className="cell-mute" dir="ltr">
          {lead.email ?? '—'}
        </td>
      );

    case 'industry':
      return <td className="cell-mute">{lead.industry ?? '—'}</td>;

    case 'city':
      return <td className="cell-mute">{lead.city ?? '—'}</td>;

    case 'address':
      return <td className="cell-mute cell-clip">{lead.address ?? '—'}</td>;

    case 'notes':
      /* الملاحظة قد تكون فقرة: تُقصّ في الخلية وتُقرأ كاملةً في الملف. */
      return (
        <td className="cell-mute cell-clip" title={lead.notes ?? undefined}>
          {lead.notes ?? '—'}
        </td>
      );

    case 'nextActionAt':
      return (
        <td className="cell-mute">{lead.nextActionAt ? dateTime(lead.nextActionAt) : '—'}</td>
      );

    case 'createdAt':
      return <td className="cell-mute">{date(lead.createdAt)}</td>;

    default:
      return <td className="cell-mute">—</td>;
  }
}
